// src/stores/workflow.ts
import { defineStore, storeToRefs } from 'pinia';
import { computed } from 'vue';

import { getClonedFieldsForNodeType } from '../utils/node-validation';

import { useWorkflowClipboard } from './workflow-clipboard';
import { useWorkflowConnector } from './workflow-connector';
import { useWorkflowGraph } from './workflow-graph';
import { useWorkflowHistory } from './workflow-history';
import { useWorkflowSelection } from './workflow-selection';
import { useWorkflowUI } from './workflow-ui';
import { useWorkflowValidation } from './workflow-validation';
import { useWorkflowVariables } from './workflow-variables';

import type { FlowNode, NodeCategory } from '../types/workflow';

export const useWorkflowStore = defineStore('bvWorkflow', () => {
  // Import the smaller stores
  const uiStore = useWorkflowUI();
  const validationStore = useWorkflowValidation();
  const selectionStore = useWorkflowSelection();
  const graphStore = useWorkflowGraph();
  const connectorStore = useWorkflowConnector();
  const clipboardStore = useWorkflowClipboard();
  const historyStore = useWorkflowHistory();

  const {
    isSameBranchPaste,
    pasteSingleReason,
    pasteSubtreeReason,
    canPasteSingleStep,
    canPasteStepWithChildren,
    pasteStepOptions,
  } = storeToRefs(clipboardStore);
  const variablesStore = useWorkflowVariables();

  // Computed properties that combine data from multiple stores
  const selectedNode = computed(() => {
    return selectionStore.selectedNodeId ? graphStore.flow[selectionStore.selectedNodeId] : null;
  });

  // Actions that coordinate between stores
  const openStepsMenu = (event: MouseEvent, clickedNode: FlowNode) => {
    if (uiStore.active) {
      return;
    }
    uiStore.openStepsMenu(event.clientX, event.clientY);
    selectionStore.setAddClickedNode(clickedNode);
  };

  const closeStepsMenu = () => {
    uiStore.closeStepsMenu();
    selectionStore.clearAddClickedNode();
  };

  const selectNode = (nodeId: string | number) => {
    selectionStore.selectNode(nodeId);
    // Auto-open side panel when a node is selected
    uiStore.setSidePanelVisibility(true);
  };

  const addNode = (newNode: Partial<FlowNode> & { type: string; category: NodeCategory }) => {
    if (!selectionStore.addClickedNode) {
      return;
    }

    const nodeId = graphStore.addNode(newNode, selectionStore.addClickedNode);
    if (nodeId) {
      // Validate the newly created node immediately
      const node = graphStore.flow[nodeId];
      if (node) {
        const clonedFields = getClonedFieldsForNodeType(node.type);
        if (clonedFields) {
          validationStore.validateNode(nodeId, clonedFields, node.data || {});
        }
      }
      selectNode(nodeId);
    }
    // Clear the clicked node after adding
    selectionStore.clearAddClickedNode();
  };

  // Copy/paste logic is handled by clipboard store
  const copyNode = clipboardStore.copyNode;

  // Paste just the copied step (without sub-steps)
  const pasteSingleStep = () => {
    const newNodeId = clipboardStore.pasteSingle();
    if (newNodeId) {
      selectNode(newNodeId);
      closeStepsMenu();
    }
  };

  // Paste the copied step with all its sub-steps
  const pasteStepWithChildren = () => {
    const newNodeId = clipboardStore.pasteWithSubtree();
    if (newNodeId) {
      selectNode(newNodeId);
      closeStepsMenu();
    }
  };

  const deleteNode = (nodeId: string | number, skipConfirmation?: boolean, skipHistory?: boolean) => {
    // Check if node can be deleted
    if (!graphStore.canDeleteNode(nodeId)) {
      return;
    }

    // if the node is ask-question, delete the variable
    if (graphStore.flow[nodeId].type === 'AskQuestion') {
      const saveResponse = graphStore.flow[nodeId].data.saveResponse;
      if (saveResponse && typeof saveResponse === 'object' && 'variable' in saveResponse) {
        const variable = (saveResponse as { variable?: string }).variable;
        if (typeof variable === 'string') {
          variablesStore.deleteVariable(variable);
        }
      }
    }

    // Delete from graph
    graphStore.deleteNode(nodeId, skipConfirmation, skipHistory);

    // Deselect if needed
    if (selectionStore.selectedNodeId === nodeId) {
      selectionStore.selectedNodeId = '__root__';
    }

    // Clear any validation errors for this node
    validationStore.clearNodeErrors(nodeId);
  };

  const deleteNodeConfirmed = (nodeId: string | number) => {
    // Check if node can be deleted
    if (!graphStore.canDeleteNode(nodeId)) {
      return;
    }

    // if the node is ask-question, delete the variable
    if (graphStore.flow[nodeId].type === 'AskQuestion') {
      const saveResponse = graphStore.flow[nodeId].data.saveResponse;
      if (saveResponse && typeof saveResponse === 'object' && 'variable' in saveResponse) {
        const variable = (saveResponse as { variable?: string }).variable;
        if (typeof variable === 'string') {
          variablesStore.deleteVariable(variable);
        }
      }
    }

    // Delete from graph (skip confirmation since user already confirmed)
    graphStore.deleteNode(nodeId, true);

    // Deselect if needed
    if (selectionStore.selectedNodeId === nodeId) {
      selectionStore.selectedNodeId = '__root__';
    }

    // Clear any validation errors for this node
    validationStore.clearNodeErrors(nodeId);
  };

  const updateNodeData = (nodeId: string | number, data: Record<string, any>) => {
    graphStore.updateNodeData(nodeId, data);
  };

  const updateNodeTriggerType = (nodeId: string | number, triggerType: any) => {
    graphStore.updateNodeTriggerType(nodeId, triggerType);
  };

  const updateNodeName = (nodeId: string | number, newName: string) => {
    graphStore.updateNodeName(nodeId, newName);
  };

  const setFlow = (flow: any) => {
    graphStore.setFlow(flow);
  };

  const reset = () => {
    uiStore.setActive(false);
    uiStore.closeStepsMenu();
    graphStore.reset();
    selectionStore.reset();
    validationStore.clearAllErrors();
  };

  const setDefaultValues = (nodeId: string | number) => {
    graphStore.setDefaultValues(nodeId);
  };

  const initializeRootNode = () => {
    graphStore.initializeRootNode();
    setTimeout(() => {
      selectionStore.selectNode('__root__');
    }, 100);
  };

  const exportFlow = () => {
    graphStore.exportFlow();
  };

  // Return the unified interface
  return {
    // UI State
    showStepsMenu: computed(() => uiStore.showStepsMenu),
    showSidePanel: computed(() => uiStore.showSidePanel),
    menuPosition: computed(() => uiStore.menuPosition),
    isActive: computed(() => uiStore.active),

    // Selection State
    selectedNode,
    selectedNodeId: computed(() => selectionStore.selectedNodeId),
    addClickedNode: computed(() => selectionStore.addClickedNode),
    copiedNode: computed(() => selectionStore.copiedNode),

    // Graph State
    flow: computed(() => graphStore.flow),
    service: computed(() => graphStore.service),
    MAX_STEPS: computed(() => graphStore.MAX_STEPS),
    lastNode: computed(() => graphStore.getLastCreatedNode), // excludes connectors
    notSupportedNodeTypes: computed(() => graphStore.notSupportedNodeTypes),

    // History State
    canUndo: computed(() => historyStore.canUndo),
    canRedo: computed(() => historyStore.canRedo),

    // Validation State
    nodeErrors: computed(() => validationStore.nodeErrors),

    // UI Actions
    setActive: uiStore.setActive,
    toggleSidePanel: uiStore.toggleSidePanel,
    openStepsMenu,
    closeStepsMenu,

    // Selection Actions
    selectNode,

    // Graph Actions
    addNode,
    copyNode,
    pasteSingleStep,
    pasteStepWithChildren,
    deleteNode,
    updateNodeData,
    updateNodeTriggerType,
    updateNodeName,
    setFlow,
    reset,
    setDefaultValues,
    initializeRootNode,
    exportFlow,
    getAvailableNodesForSelection: graphStore.getAvailableNodesForSelection,
    setService: graphStore.setService,
    setNotSupportedNodeTypes: graphStore.setNotSupportedNodeTypes,
    isNodeTypeNotSupported: graphStore.isNodeTypeNotSupported,

    // History Actions
    undo: historyStore.undo,
    redo: historyStore.redo,
    addToHistory: historyStore.addToHistoryTransaction,
    startHistory: historyStore.startHistoryTransaction,
    endHistory: historyStore.endHistoryTransaction,
    resetHistory: historyStore.resetHistory,
    replaceNodeDataInHistory: historyStore.replaceNodeDataInHistory,

    // Validation Actions
    updateNodeFieldError: validationStore.updateNodeFieldError,
    clearNodeErrors: validationStore.clearNodeErrors,
    clearAllErrors: validationStore.clearAllErrors,
    validateNode: validationStore.validateNode,
    setSpecialNodesErrors: validationStore.setSpecialNodesErrors,
    hasSpecialNodesErrors: computed(() =>
      validationStore.specialNodesErrors.some((entry) => entry.errorNodeId in graphStore.flow),
    ),

    // Getters from graph store
    getNodesByCategory: graphStore.getNodesByCategory,
    canDeleteNode: graphStore.canDeleteNode,
    totalSteps: computed(() => graphStore.totalSteps),
    getFlow: computed(() => graphStore.getFlow),
    canPasteSingleStep,
    canPasteStepWithChildren,
    pasteSingleReason,
    pasteSubtreeReason,
    isSameBranchPaste,
    pasteStepOptions,

    // Connector management actions
    removeConnectorNode: (routerNodeId: string | number, connectorNodeId: string | number, skipHistory?: boolean) =>
      connectorStore.removeConnectorNode(routerNodeId, connectorNodeId, skipHistory),
    addConnector: (parentKey: string | number, connectorConfig: any, skipHistory?: boolean) =>
      connectorStore.addConnector(graphStore.flow, parentKey, connectorConfig, skipHistory),
    getConnectors: (parentKey: string | number) => connectorStore.getConnectors(graphStore.flow, parentKey),
    canAddConnector: (parentKey: string | number) => connectorStore.canAddConnector(graphStore.flow, parentKey),
    canRemoveConnector: (parentKey: string | number) => connectorStore.canRemoveConnector(graphStore.flow, parentKey),
    generateConnectorTitle: (
      parentKey: string | number,
      baseTitle: string,
      connectorType?: string,
      numberingKey?: string,
    ) => connectorStore.generateConnectorTitle(graphStore.flow, parentKey, baseTitle, connectorType, numberingKey),

    // Getters from validation store
    getNodeErrors: validationStore.getNodeErrors,
    hasNodeErrors: validationStore.hasNodeErrors,
    isNodeUnavailable: validationStore.isNodeUnavailable,
    getNodesWithErrors: computed(() => validationStore.getNodesWithErrors),
    getTotalErrorCount: computed(() => validationStore.getTotalErrorCount),

    // Variables store
    variables: computed(() => variablesStore.getVariables),
    getVariable: variablesStore.getVariable,
    getAvailableVariablesForNode: variablesStore.getAvailableVariablesForNode,
    getVariableNames: computed(() => variablesStore.getVariableNames),
    hasVariable: variablesStore.hasVariable,
    setVariable: variablesStore.setVariable,
    deleteVariable: variablesStore.deleteVariable,
    clearVariables: variablesStore.clearVariables,
    fetchCustomAttributesVariables: variablesStore.fetchCustomAttributesVariables,

    // Confirmation dialog
    confirmationDialog: computed(() => graphStore.confirmationDialog),
    confirmDeletion: () => {
      // Get the node ID from the confirmation dialog
      const nodeId = graphStore.confirmationDialog.nodeId;
      if (nodeId) {
        // Call the confirmed deletion method which skips confirmation dialog
        deleteNodeConfirmed(nodeId);
      }
      // Close the confirmation dialog
      graphStore.closeConfirmationDialog();
    },
    cancelDeletion: graphStore.cancelDeletion,

    // Step configuration
    getStepConfig: connectorStore.getStepConfig,
  };
});
